import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { useProfile, useUpdateProfile, useUploadLogo } from "@/hooks/useProfile";
import { ArrowLeft, Upload, User, Building, Image, Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const uploadLogo = useUploadLogo();
  
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [copied, setCopied] = useState(false);

  const copyForwardingEmail = () => {
    if (profile?.forwarding_email) {
      navigator.clipboard.writeText(profile.forwarding_email);
      setCopied(true);
      toast.success("Email address copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setCompanyName(profile.company_name || "");
    }
  }, [profile]);

  const handleSave = async () => {
    await updateProfile.mutateAsync({
      full_name: fullName,
      company_name: companyName,
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be less than 2MB");
      return;
    }
    
    await uploadLogo.mutateAsync(file);
  };

  if (authLoading || profileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      
      <main className="flex-1 py-8">
        <div className="container max-w-2xl">
          <Link 
            to="/trips" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Trips
          </Link>
          
          <h1 className="font-display text-3xl font-bold text-foreground">Profile</h1>
          <p className="mt-2 text-muted-foreground">
            Manage your account settings and branding
          </p>

          <div className="mt-8 space-y-8">
            {/* Email Forwarding Section */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h2 className="font-display text-lg font-semibold text-card-foreground flex items-center gap-2">
                <Mail className="h-5 w-5 text-accent" />
                Email Forwarding
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Forward your travel confirmation emails to automatically import trips
              </p>
              
              <div className="mt-4">
                <Label>Your Forwarding Address</Label>
                <div className="mt-2 flex items-center gap-2">
                  <Input 
                    value={profile?.forwarding_email || ""}
                    readOnly
                    className="bg-muted font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyForwardingEmail}
                    disabled={!profile?.forwarding_email}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Forward booking confirmations from airlines, hotels, and rental cars to this address
                </p>
              </div>
            </div>

            {/* Logo Section */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h2 className="font-display text-lg font-semibold text-card-foreground flex items-center gap-2">
                <Image className="h-5 w-5 text-accent" />
                Logo Branding
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Upload your company or personal logo to appear on itineraries
              </p>
              
              <div className="mt-6 flex items-center gap-6">
                <div className="h-20 w-32 rounded-lg border-2 border-dashed border-border bg-muted/50 flex items-center justify-center overflow-hidden">
                  {profile?.logo_url ? (
                    <img 
                      src={profile.logo_url} 
                      alt="Logo" 
                      className="h-full w-full object-contain p-2"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">No logo</span>
                  )}
                </div>
                
                <div>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <label htmlFor="logo-upload">
                    <Button 
                      variant="outline" 
                      asChild 
                      className="cursor-pointer"
                      disabled={uploadLogo.isPending}
                    >
                      <span>
                        <Upload className="mr-2 h-4 w-4" />
                        {uploadLogo.isPending ? "Uploading..." : "Upload Logo"}
                      </span>
                    </Button>
                  </label>
                  <p className="mt-2 text-xs text-muted-foreground">
                    PNG, JPG up to 2MB. Recommended: 300x100px
                  </p>
                </div>
              </div>
            </div>

            {/* Profile Info Section */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h2 className="font-display text-lg font-semibold text-card-foreground flex items-center gap-2">
                <User className="h-5 w-5 text-accent" />
                Personal Information
              </h2>
              
              <div className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email"
                    value={user?.email || ""}
                    disabled
                    className="mt-2 bg-muted"
                  />
                </div>
                
                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input 
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your full name"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>

            {/* Company Section */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h2 className="font-display text-lg font-semibold text-card-foreground flex items-center gap-2">
                <Building className="h-5 w-5 text-accent" />
                Company Details
              </h2>
              
              <div className="mt-6">
                <Label htmlFor="companyName">Company Name</Label>
                <Input 
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your company or agency name"
                  className="mt-2"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  This may appear on your generated itineraries
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSave}
                disabled={updateProfile.isPending}
                variant="accent"
                size="lg"
              >
                {updateProfile.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
